import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';

interface Tiles {
  readonly totalClaims: number;
  readonly draftClaims: number;
  readonly submittedClaims: number;
  readonly pendingVerification: number;
  readonly approvedForFinance: number;
  readonly returnedClaims: number;
  readonly rejectedClaims: number;
  readonly withdrawnClaims: number;
  readonly totalBillingAmount: number;
}

interface QueueItem {
  readonly claimId: string;
  /** The job, dealer and date the bill is known by. */
  readonly bill: BillRef | null;
  readonly vendorCode: string | null;
  readonly vendorName: string | null;
  readonly status: string;
  readonly totalClaimAmount: number;
  readonly createdAt: string;
}

const TILE_STYLE: React.CSSProperties = {
  padding: 16, borderRadius: 8,
  border: '1px solid var(--color-border-default, #d0d7de)',
  background: 'var(--color-bg-elevated, #fff)',
  textAlign: 'center', minWidth: 120,
};

const NUM_STYLE: React.CSSProperties = {
  fontSize: 'var(--font-size-2xl, 28px)', fontWeight: 700,
  color: 'var(--color-brand-primary, #0969da)',
};


export function BillingDashboardPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay, day } = useBusinessDay();
  const [tiles, setTiles] = useState<Tiles | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      apiRequest<Tiles>('/v1/billing/dashboard/tiles', { token }),
      apiRequest<{ items: QueueItem[]; total: number }>('/v1/billing/dashboard/queue', { token }),
    ])
      .then(([t, q]) => {
        if (!alive) return;
        setTiles(t); setQueue(q.items ?? []);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <main aria-label="billing-dashboard"><p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p></main>;
  if (!tiles) return <main aria-label="billing-dashboard"><p>{t('billing.dashboard.loading')}</p></main>;

  return (
    <main aria-label="billing-dashboard">
      <h1 style={{ marginBottom: 16 }}>{t('billing.dashboard.title')}</h1>

      {/* ── Tile grid ────────────────────────────────── */}
      <section aria-label="billing-tiles" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        {([
          ['total-claims', t('billing.dashboard.tile.total'), tiles.totalClaims],
          ['draft', t('billing.dashboard.tile.draft'), tiles.draftClaims],
          ['submitted', t('billing.dashboard.tile.submitted'), tiles.submittedClaims],
          ['pending-verification', t('billing.dashboard.tile.pending'), tiles.pendingVerification],
          ['approved-for-finance', t('billing.dashboard.tile.approved'), tiles.approvedForFinance],
          ['returned', t('billing.dashboard.tile.returned'), tiles.returnedClaims],
          ['rejected', t('billing.dashboard.tile.rejected'), tiles.rejectedClaims],
          ['withdrawn', t('billing.dashboard.tile.withdrawn'), tiles.withdrawnClaims],
        ] as const).map(([key, label, val]) => (
          <div key={key} style={TILE_STYLE} data-testid={`tile-${key}`}>
            <div style={NUM_STYLE}>{val}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted, #656d76)' }}>{label}</div>
          </div>
        ))}
        <div style={TILE_STYLE} data-testid="tile-total-billing-amount">
          <div style={NUM_STYLE}>₹{money(tiles.totalBillingAmount)}</div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted, #656d76)' }}>{t('billing.dashboard.tile.totalAmount')}</div>
        </div>
      </section>

      {/* ── Recent claims queue ──────────────────────── */}
      <section aria-label="billing-queue">
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('billing.dashboard.recentClaims')}</h2>
        {queue.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted, #656d76)' }}>{t('billing.dashboard.noClaims')}</p>
        ) : (
          <table aria-label="billing-queue-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border-default, #d0d7de)', textAlign: 'left' }}>
                <th style={{ padding: '8px 4px' }}>{t('billing.dashboard.col.claim')}</th>
                <th style={{ padding: '8px 4px' }}>{t('billing.dashboard.col.vendor')}</th>
                <th style={{ padding: '8px 4px' }}>{t('billing.dashboard.col.status')}</th>
                <th style={{ padding: '8px 4px' }}>{t('billing.dashboard.col.amount')}</th>
                <th style={{ padding: '8px 4px' }}>{t('billing.dashboard.col.created')}</th>
                <th style={{ padding: '8px 4px' }}></th>
              </tr>
            </thead>
            <tbody>
              {queue.map((r) => (
                <tr key={r.claimId} aria-label={`row-${r.claimId}`}
                    style={{ borderBottom: '1px solid var(--color-border-default, #d0d7de)' }}>
                  <td style={{ padding: '8px 4px' }}>{billRefLabel(r.bill, day)}</td>
                  <td style={{ padding: '8px 4px' }}>
                    <div>{r.vendorName ?? '—'}</div>
                    <div style={{ color: 'var(--color-text-muted, #656d76)' }}>{r.vendorCode}</div>
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    <span aria-label={`status-${r.claimId}`}>{r.status}</span>
                  </td>
                  <td style={{ padding: '8px 4px' }}>₹{money(r.totalClaimAmount)}</td>
                  <td style={{ padding: '8px 4px' }}>{atDay(r.createdAt)}</td>
                  <td style={{ padding: '8px 4px' }}>
                    <Link to={`/billing/claims/${r.claimId}`} data-testid={`link-claim-${r.claimId}`}>{t('billing.dashboard.view')}</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}
